import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable, ModalForm, ProFormDigit, ProFormRadio, PageContainer } from '@ant-design/pro-components';
import { Button, Grid, message, Popconfirm, Tag } from 'antd';
import { useRef, useState } from 'react';
import api from '../services/api';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';

type ActivationCodeRow = {
  id: number;
  code: string;
  validityType: 'day' | 'year' | 'permanent';
  isUsed: boolean;
  isExported: boolean;
  isVoided: boolean;
  voidedAt?: string | Date | null;
  voidedByAdminUsername?: string | null;
  usedBy?: { username: string } | null;
  usedAt?: string | Date | null;
  createdAt: string | Date;
};

type CountFormValues = { count: number; validityType: 'day' | 'year' | 'permanent' };

const formatDateTime = (value: unknown) => {
  if (!value) return '-';
  const date =
    value instanceof Date
      ? value
      : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';

  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

const formatValidityType = (value: ActivationCodeRow['validityType'] | undefined) => {
  if (value === 'day') return '一天';
  if (value === 'year') return '一年';
  return '永久';
};

export default function Dashboard() {
  const actionRef = useRef<ActionType | undefined>(undefined);
  const [exportLoading, setExportLoading] = useState(false);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const handleMarkExported = async (record: ActivationCodeRow) => {
    try {
      await api.post(`/admin/activation-codes/${record.id}/mark-exported`);
      message.success('已标记为已导出');
      actionRef.current?.reload();
    } catch {
      message.error('标记失败');
    }
  };

  const handleVoid = async (record: ActivationCodeRow) => {
    try {
      await api.post(`/admin/activation-codes/${record.id}/void`);
      message.success('作废成功');
      actionRef.current?.reload();
    } catch {
      message.error('作废失败');
    }
  };

  const columns: ProColumns<ActivationCodeRow>[] = [
    {
      title: 'ID',
      dataIndex: 'id',
      search: false,
      width: 80,
      hideInTable: isMobile,
    },
    {
      title: '激活码',
      dataIndex: 'code',
      copyable: true,
      width: isMobile ? 180 : 200,
    },
    {
      title: '有效期',
      dataIndex: 'validityType',
      width: 100,
      valueEnum: {
        day: { text: '一天' },
        year: { text: '一年' },
        permanent: { text: '永久' },
      },
      render: (_, record) => formatValidityType(record.validityType),
    },
    {
      title: '状态',
      dataIndex: 'isUsed',
      width: 100,
      valueEnum: {
        false: { text: '未使用', status: 'Success' },
        true: { text: '已使用', status: 'Error' },
      },
      render: (_, record) => {
        if (record.isVoided) return <Tag>已作废</Tag>;
        if (record.isUsed) return <Tag color="red">已使用</Tag>;
        return <Tag color="green">未使用</Tag>;
      },
    },
    {
      title: '作废状态',
      dataIndex: 'isVoided',
      hideInTable: true,
      valueEnum: {
        false: { text: '未作废' },
        true: { text: '已作废' },
      },
    },
    {
      title: '导出状态',
      dataIndex: 'isExported',
      width: 100,
      hideInTable: isMobile,
      valueEnum: {
        false: { text: '未导出', status: 'Default' },
        true: { text: '已导出', status: 'Processing' },
      },
    },
    {
      title: '使用者',
      dataIndex: ['usedBy', 'username'],
      search: false,
      hideInTable: isMobile,
      render: (_, record) => record.usedBy ? record.usedBy.username : '-',
    },
    {
      title: '使用时间',
      dataIndex: 'usedAt',
      valueType: 'dateTime',
      search: false,
      hideInTable: isMobile,
      render: (_, record) => formatDateTime(record.usedAt),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      valueType: 'dateTime',
      search: false,
      sorter: true,
      hideInTable: isMobile,
    },
    {
      title: '操作',
      valueType: 'option',
      width: 160,
      render: (_, record) => {
        const voidDisabled = record.isUsed || record.isVoided;
        const exportDisabled = record.isUsed || record.isVoided || record.isExported;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Popconfirm
              key="void"
              title="确认作废该激活码？"
              description="作废后将无法用于注册或续期"
              okText="确认"
              cancelText="取消"
              disabled={voidDisabled}
              onConfirm={() => handleVoid(record)}
            >
              <Button type="link" danger disabled={voidDisabled} style={{ padding: 0 }}>
                作废
              </Button>
            </Popconfirm>
            <Popconfirm
              key="markExported"
              title="确认标记为已导出？"
              okText="确认"
              cancelText="取消"
              disabled={exportDisabled}
              onConfirm={() => handleMarkExported(record)}
            >
              <Button type="link" disabled={exportDisabled} style={{ padding: 0 }}>
                标记已导出
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const handleBatchCreate = async (values: CountFormValues) => {
    await api.post('/admin/activation-codes/batch', { count: values.count, validityType: values.validityType });
    message.success(`成功创建 ${values.count} 个激活码（${formatValidityType(values.validityType)}）`);
    actionRef.current?.reload();
    return true;
  };

  const handleExport = async (values: CountFormValues) => {
    try {
        setExportLoading(true);
        const res = await api.post('/admin/activation-codes/export', { count: values.count, validityType: values.validityType });
        const codes = res.data as ActivationCodeRow[];
        if (codes.length === 0) {
            message.warning('没有可导出的未使用激活码');
            return true;
        }
        
        const csvContent = "data:text/csv;charset=utf-8,\uFEFF" 
            + "ID,激活码,有效期,创建时间\n"
            + codes.map((c) => `${c.id},${c.code},${formatValidityType(c.validityType)},${formatDateTime(c.createdAt)}`).join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `activation_codes_${values.validityType}_${new Date().getTime()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        message.success(`成功导出 ${codes.length} 个激活码（${formatValidityType(values.validityType)}）`);
        actionRef.current?.reload();
        return true;
    } catch {
        message.error('导出失败');
        return false;
    } finally {
        setExportLoading(false);
    }
  };

  return (
    <PageContainer
      childrenContentStyle={
        isMobile
          ? {
              paddingInline: 12,
              paddingBlock: 12,
            }
          : undefined
      }
    >
      <ProTable
        columns={columns}
        actionRef={actionRef}
        cardBordered
        tableLayout="fixed"
        scroll={isMobile ? { x: 'max-content' } : undefined}
        search={{
          labelWidth: 'auto',
          span: isMobile ? 24 : undefined,
          defaultCollapsed: true,
        }}
        request={async (params: {
          current?: number;
          pageSize?: number;
          isUsed?: boolean;
          isExported?: boolean;
          isVoided?: boolean;
          code?: string;
          validityType?: ActivationCodeRow['validityType'];
        } = {}) => {
          const { current, pageSize, isUsed, isExported, isVoided, code, validityType } = params;
          const res = await api.get('/admin/activation-codes', {
              params: { 
                page: current, 
                limit: pageSize,
                isUsed,
                isExported,
                isVoided,
                code,
                validityType,
              }
          });
          return {
              data: res.data.items,
              success: true,
              total: res.data.total,
          };
        }}
        rowKey="id"
        options={{
          setting: {
            listsHeight: 400,
          },
        }}
        form={{
          syncToUrl: (values, type) => {
            if (type === 'get') {
              return {
                ...values,
                created_at: [values.startTime, values.endTime],
              };
            }
            return values;
          },
        }}
        pagination={{
          pageSize: 10,
        }}
        dateFormatter="string"
        headerTitle="激活码列表"
        toolBarRender={() => [
          <ModalForm
              title="批量创建激活码"
              trigger={
                  <Button key="button" icon={<PlusOutlined />} type="primary" block={isMobile}>
                      新建
                  </Button>
              }
              onFinish={handleBatchCreate}
          >
              <ProFormDigit
                  label="数量"
                  name="count"
                  min={1}
                  max={10000}
                  initialValue={10}
                  rules={[{ required: true }]}
              />
              <ProFormRadio.Group
                label="有效期"
                name="validityType"
                initialValue="permanent"
                options={[
                  { label: '一天', value: 'day' },
                  { label: '一年', value: 'year' },
                  { label: '永久', value: 'permanent' },
                ]}
                rules={[{ required: true }]}
              />
          </ModalForm>,
          <ModalForm
              title="导出未使用激活码"
              trigger={
                  <Button key="export" icon={<DownloadOutlined />} loading={exportLoading} block={isMobile}>
                      导出
                  </Button>
              }
              onFinish={handleExport}
          >
              <ProFormDigit
                  label="导出数量"
                  name="count"
                  min={1}
                  max={10000}
                  initialValue={50}
                  rules={[{ required: true }]}
              />
              <ProFormRadio.Group
                label="有效期"
                name="validityType"
                initialValue="permanent"
                options={[
                  { label: '一天', value: 'day' },
                  { label: '一年', value: 'year' },
                  { label: '永久', value: 'permanent' },
                ]}
                rules={[{ required: true }]}
              />
          </ModalForm>
        ]}
      />
    </PageContainer>
  );
}
